"use client";

import * as React from "react";
import { DateTime } from "luxon";
import { LuCheck, LuGlobe, LuSearch } from "react-icons/lu";

import { cn } from "~/utils/fontsConfig";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInputV4,
    CommandItem,
    CommandList,
} from "~/components/shadcn/Command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/shadcn/Popover";
import { Input } from "~/components/v4/shadcn/Input";
import { SelectCustomization } from "~/types/CustomizableLayout";
export interface TimezoneOption {
    value: string;
    label: string;
    offset: string;
    city: string;
}

export const generateTimezoneList = (): TimezoneOption[] => {
    const now = DateTime.now();
    const zones = Intl.supportedValuesOf("timeZone");

    return zones
        .map((zone: string): TimezoneOption => {
            const dt = now.setZone(zone);
            const offset = dt.toFormat("ZZ");
            const [region, ...cityParts] = zone.split("/");

            // Ensure we always have a string for city
            const cityName =
                cityParts.length > 0
                    ? cityParts.join("/").replace(/_/g, " ")
                    : region;

            const timezone: TimezoneOption = {
                value: zone,
                label: `${region}/${cityName}`,
                offset,
                city: cityName ?? "",
            };

            return timezone;
        })
        .sort((a: TimezoneOption, b: TimezoneOption): number => {
            const offsetA = parseFloat(a.offset.replace("+", "")) || 0;
            const offsetB = parseFloat(b.offset.replace("+", "")) || 0;

            if (offsetA === offsetB) {
                // We know city is always defined because we set it above
                return a.city.localeCompare(b.city);
            }
            return offsetA - offsetB;
        });
};

const timezones = generateTimezoneList();

interface TimezoneSelectProps {
    value?: string;
    onChange?: (value: string) => void;
    className?: string;
    disabled?: boolean;
    customization?: SelectCustomization;
}

export function TimezoneSelect({
    value,
    onChange,
    className,
    disabled,
    customization,
}: TimezoneSelectProps) {
    const [open, setOpen] = React.useState(false);
    const [selectedTimezone, setSelectedTimezone] = React.useState<
        TimezoneOption | undefined
    >(() => timezones.find((tz) => tz.value === value) || timezones[0]);

    React.useEffect(() => {
        if (value) {
            const matchedTimezone = timezones.find((tz) => tz.value === value);
            if (matchedTimezone) {
                setSelectedTimezone(matchedTimezone);
                onChange?.(matchedTimezone.value);
                return;
            }
        }

        const browserTimezone = DateTime.local().zoneName;
        const defaultTimezone =
            timezones.find((tz) => tz.value === browserTimezone) ||
            timezones[0];

        if (!value && defaultTimezone) {
            setSelectedTimezone(defaultTimezone);
            onChange?.(defaultTimezone.value);
        }
    }, [value, onChange]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <div className="relative w-full">
                    <Input
                        value={
                            selectedTimezone
                                ? `${selectedTimezone.label} (${selectedTimezone.offset})`
                                : value
                        }
                        disabled={disabled}
                        role="combobox"
                        aria-expanded={open}
                        placeholder="Select timezone..."
                        className={cn(
                            "cursor-pointer ",
                            open && "border border-v3-clover",
                            className,
                            customization?.inputClassName,
                        )}
                        prefixComponent={
                            <LuGlobe className="h-4 w-4 shrink-0 text-zinc-400" />
                        }
                        suffixComponent={
                            <LuSearch className="h-4 w-4 shrink-0 opacity-50" />
                        }
                        readOnly
                    />
                </div>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] border-0 p-0">
                <Command className="w-full">
                    <CommandInputV4
                        placeholder="Search timezone..."
                        className={cn(
                            "h-9 rounded-md",
                            customization?.inputClassName,
                        )}
                        wrapperClass={customization?.inputClassName}
                    />
                    <CommandList className={cn(customization?.listClassName)}>
                        <CommandEmpty>No timezone found.</CommandEmpty>
                        <CommandGroup>
                            {timezones.map((timezone) => (
                                <TimezoneItem
                                    key={timezone.value}
                                    timezone={timezone}
                                    selectedTimezone={selectedTimezone}
                                    className={customization?.itemClassName}
                                    onSelect={() => {
                                        setSelectedTimezone(timezone);
                                        onChange?.(timezone.value);
                                        setOpen(false);
                                    }}
                                />
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

function TimezoneItem({
    timezone,
    selectedTimezone,
    onSelect,
    className,
}: {
    timezone: TimezoneOption;
    selectedTimezone?: TimezoneOption;
    onSelect: () => void;
    className?: string;
}) {
    return (
        <CommandItem
            value={timezone.value}
            onSelect={onSelect}
            className={cn(className)}
        >
            <div className="flex flex-col">
                <div className="flex items-center">
                    <span>{timezone.label}</span>
                    <span className="ml-2 text-sm text-muted-foreground">
                        (GMT{timezone.offset})
                    </span>
                    {selectedTimezone?.value === timezone.value && (
                        <LuCheck className="ml-auto h-4 w-4" />
                    )}
                </div>
                <span className="text-sm text-muted-foreground">
                    {timezone.city}
                </span>
            </div>
        </CommandItem>
    );
}
